import type {
  HealthResponse,
  DashboardResponse,
  CostResponse,
  EntityListResponse,
  ComponentHealthResponse,
  ActiveDispatch,
  HeroesResponse,
  DispatchDetail,
  DispatchesResponse,
  DispatchTimelineResponse,
  DiagnosticsResponse,
  DigitalThreadHealthResponse,
  DocsListResponse,
  DocsContentResponse,
  CostPacingData,
  SessionHandoffResponse,
  OpsReportResponse,
  ConsultationSummaryResponse,
  DiagnosticReportResponse,
  DispatchMetricsResponse,
  SnapshotResponse,
  VenturesResponse,
  FrictionFunnelMetrics,
  WaveFunnelResponse,
  DispatchHealthResponse,
  PhaseProgressResponse,
  HomeDigestResponse,
  ReleaseValidationsResponse,
  DailyDigestResponse,
  CIQueueDepthResponse,
  VlrArrivalRateGaugeResponse,
} from "./types.js";

const API_URL_KEY = "alkio_api_url";
const SELECTED_VENTURE_KEY = "alkio_selected_venture";

/**
 * The "home" base — where the operator originally pointed this PWA install
 * (Settings' API URL field). Per DEC-164 (per-venture Postgres + federation),
 * only the Alkio instance holds portfolio/ventures.json, so the venture LIST
 * is always fetched from the home base regardless of which venture is
 * currently selected for viewing (see `fetchVentures`).
 */
export function getHomeApiUrl(): string {
  return (
    localStorage.getItem(API_URL_KEY) ??
    (import.meta.env.VITE_API_URL as string | undefined) ??
    ""
  );
}

export function setApiUrl(url: string): void {
  localStorage.setItem(API_URL_KEY, url.replace(/\/$/, ""));
}

export interface SelectedVenture {
  id: string;
  name: string;
  apiBase: string;
}

/**
 * The venture currently selected for viewing (venture switcher, pulled
 * forward from Phase-9). `null` means "no override" — the home base is used,
 * which defaults to the Alkio instance. Selecting "Alkio" clears the
 * override rather than storing an explicit entry, so the default stays the
 * home base even if Alkio's apiBase drifts.
 */
export function getSelectedVenture(): SelectedVenture | null {
  const raw = localStorage.getItem(SELECTED_VENTURE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<SelectedVenture>;
    if (!parsed.id || !parsed.name || !parsed.apiBase) return null;
    return parsed as SelectedVenture;
  } catch {
    return null;
  }
}

export function setSelectedVenture(venture: SelectedVenture | null): void {
  if (!venture || venture.id === "alkio") {
    localStorage.removeItem(SELECTED_VENTURE_KEY);
    return;
  }
  localStorage.setItem(SELECTED_VENTURE_KEY, JSON.stringify(venture));
}

/**
 * The API base for all PWA data calls — the selected venture's apiBase when
 * one is set and reachable-looking (non-empty), otherwise the home base.
 * This is base-URL switching per DEC-164, not a tenant query param: each
 * venture is its own instance.
 */
export function getApiUrl(): string {
  const selected = getSelectedVenture();
  if (selected?.apiBase) return selected.apiBase;
  return getHomeApiUrl();
}

export function isConfigured(): boolean {
  return getApiUrl().length > 0;
}

let _isAuthenticated = true;
let _onUnauthenticated: (() => void) | null = null;

export function registerUnauthenticatedHandler(fn: () => void): void {
  _onUnauthenticated = fn;
}

export function getIsAuthenticated(): boolean {
  return _isAuthenticated;
}

/**
 * Module-level latch — a burst of parallel 401s (several in-flight fetches
 * all expiring at once) must trigger exactly ONE top-level navigation, not
 * a stack of redirects. Reset only by a fresh page load (this module is
 * re-evaluated on navigation), which is exactly when it should reset.
 */
let _redirecting = false;

export function isRedirectingToSignIn(): boolean {
  return _redirecting;
}

/**
 * Distinguishes an auth-expiry Error (thrown by get()/put() on 401 or an
 * oauth2-proxy opaque redirect) from a genuine connectivity failure
 * (network error, timeout, 5xx). Callers use this to choose between an
 * "AUTH_EXPIRED — redirecting to sign-in…" banner and a real
 * "API unreachable" banner — the operator must never see the latter for
 * what is actually an auth expiry (Jul-8 trap).
 */
export function isAuthError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const msg = err.message.toLowerCase();
  return msg.includes("401") || msg.includes("unauthenticated");
}

/**
 * Full top-level navigation (NOT a fetch) to the API origin's oauth2-proxy
 * sign-in page, carrying the current PWA URL as `rd` so the Google OAuth
 * roundtrip lands back where the operator was — on whichever origin is
 * currently serving the PWA (pwa.alkio.net or the *.pages.dev preview),
 * both now whitelisted server-side (#2113/#2114). A fetch() cannot recover
 * from an expired oauth2-proxy session: only a real navigation reaches the
 * Google login flow.
 */
export function triggerSignInRedirect(): void {
  if (_redirecting) return;
  _redirecting = true;
  const apiUrl = getApiUrl();
  const rd = encodeURIComponent(window.location.href);
  window.location.assign(`${apiUrl}/oauth2/sign_in?rd=${rd}`);
}

function handleUnauthenticated(): void {
  _isAuthenticated = false;
  _onUnauthenticated?.();
  window.dispatchEvent(new CustomEvent("pwa-unauthenticated"));
  triggerSignInRedirect();
}

/**
 * GET timeout — a hung request must eventually REJECT so screens can render
 * an explicit error state instead of sitting on "Loading…"/"CONNECTING..."
 * forever (DEC-166: a connectivity failure must never read as calm).
 */
const GET_TIMEOUT_MS = 15_000;

async function get<T>(path: string, baseOverride?: string): Promise<T> {
  const base = baseOverride ?? getApiUrl();
  const res = await fetch(`${base}${path}`, {
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    redirect: "manual",
    signal: AbortSignal.timeout(GET_TIMEOUT_MS),
  });
  // oauth2-proxy returns a 302 to the Google login when unauthenticated. With
  // redirect: "manual" the browser surfaces this as an opaque redirect — treat
  // as unauthenticated so App renders the sign-in screen.
  if (res.type === "opaqueredirect") {
    handleUnauthenticated();
    throw new Error("Unauthenticated");
  }
  if (res.status === 401) {
    handleUnauthenticated();
    throw new Error("401: Unauthenticated");
  }
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

async function put<T>(path: string, body: unknown): Promise<T> {
  const base = getApiUrl();
  const res = await fetch(`${base}${path}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    redirect: "manual",
    body: JSON.stringify(body),
  });
  if (res.type === "opaqueredirect") {
    handleUnauthenticated();
    throw new Error("Unauthenticated");
  }
  if (res.status === 401) {
    handleUnauthenticated();
    throw new Error("401: Unauthenticated");
  }
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

export async function fetchAutoCosMode(): Promise<{ mode: string }> {
  return get<{ mode: string }>("/api/pwa/config/auto-cos-mode");
}

export async function setAutoCosMode(mode: string): Promise<{ mode: string }> {
  return put<{ mode: string }>("/api/pwa/config/auto-cos-mode", { mode });
}

/**
 * Append `as_of` (Pattern 2 — timeline scrubbing) to a path when scrubbed.
 * `asOf` null/undefined = live = no param.
 */
function withAsOf(path: string, asOf?: string | null): string {
  if (!asOf) return path;
  return `${path}${path.includes("?") ? "&" : "?"}as_of=${encodeURIComponent(asOf)}`;
}

export async function fetchEntities(
  type: string,
  options?: {
    cursor?: string;
    status?: string;
    search?: string;
    sort?: string;
    milestoneId?: string;
    /** ISO timestamp to view the list "as of" (null = live). */
    asOf?: string | null;
  },
): Promise<EntityListResponse> {
  const params = new URLSearchParams();
  if (options?.cursor) params.set("cursor", options.cursor);
  if (options?.status) params.set("status", options.status);
  if (options?.search) params.set("search", options.search);
  if (options?.sort) params.set("sort", options.sort);
  if (options?.milestoneId) params.set("milestone_id", options.milestoneId);
  if (options?.asOf) params.set("as_of", options.asOf);
  const qs = params.toString();
  const raw = await get<{
    data?: Record<string, unknown>[];
    entities?: Record<string, unknown>[];
    next_cursor: string | null;
    total: number;
    as_of?: string;
  }>(`/api/pwa/entities/${type}${qs ? `?${qs}` : ""}`);
  return {
    entities: raw.data ?? raw.entities ?? [],
    next_cursor: raw.next_cursor,
    total: raw.total,
    ...(raw.as_of ? { as_of: raw.as_of } : {}),
  };
}

export async function fetchEntity(
  type: string,
  id: string,
  asOf?: string | null,
): Promise<Record<string, unknown>> {
  const raw = await get<{ data: Record<string, unknown> }>(
    withAsOf(`/api/pwa/entities/${type}/${id}`, asOf),
  );
  return raw.data ?? raw;
}

/**
 * Fetch the portfolio venture list for the switcher. Always queried against
 * the home base (see `getHomeApiUrl`), never the currently-selected venture —
 * portfolio/ventures.json is authored on the Alkio instance only, so a
 * viewer parked on another venture must still be able to fetch the list to
 * switch back.
 */
export async function fetchVentures(): Promise<VenturesResponse> {
  return get<VenturesResponse>("/api/pwa/ventures", getHomeApiUrl());
}

export async function fetchActiveDispatches(): Promise<{
  active_dispatches: ActiveDispatch[];
  count: number;
}> {
  return get<{ active_dispatches: ActiveDispatch[]; count: number }>(
    "/api/pwa/active-dispatches",
  );
}

export async function fetchDispatches(): Promise<DispatchesResponse> {
  return get<DispatchesResponse>("/api/pwa/dispatches");
}

export async function fetchDispatchDetail(id: string): Promise<DispatchDetail> {
  return get<DispatchDetail>(`/api/pwa/dispatches/${encodeURIComponent(id)}`);
}

export async function fetchDispatchTimeline(
  id: string,
): Promise<DispatchTimelineResponse> {
  return get<DispatchTimelineResponse>(
    `/api/pwa/dispatches/${encodeURIComponent(id)}/timeline`,
  );
}

export async function cancelDispatch(
  id: string,
): Promise<{ job_id: string; status: string }> {
  const base = getApiUrl();
  const res = await fetch(
    `${base}/api/pwa/dispatches/${encodeURIComponent(id)}/cancel`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      redirect: "manual",
    },
  );
  if (res.type === "opaqueredirect") {
    handleUnauthenticated();
    throw new Error("Unauthenticated");
  }
  if (res.status === 401) {
    handleUnauthenticated();
    throw new Error("401: Unauthenticated");
  }
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${text}`);
  }
  return res.json() as Promise<{ job_id: string; status: string }>;
}

export async function fetchDiagnostics(): Promise<DiagnosticsResponse> {
  return get<DiagnosticsResponse>("/api/pwa/diagnostics");
}

export async function fetchDigitalThreadHealth(): Promise<DigitalThreadHealthResponse> {
  return get<DigitalThreadHealthResponse>("/api/pwa/digital-thread-health");
}

export async function fetchVlrArrivalRate(): Promise<VlrArrivalRateGaugeResponse> {
  return get<VlrArrivalRateGaugeResponse>("/api/pwa/vlr-arrival-rate");
}

export async function fetchDocsList(): Promise<DocsListResponse> {
  return get<DocsListResponse>("/api/pwa/docs");
}

export async function fetchDocsSearch(
  query: string,
): Promise<DocsListResponse> {
  return get<DocsListResponse>(
    `/api/pwa/docs?search=${encodeURIComponent(query)}`,
  );
}

export async function fetchDocContent(
  path: string,
): Promise<DocsContentResponse> {
  return get<DocsContentResponse>(
    `/api/pwa/docs/content?path=${encodeURIComponent(path)}`,
  );
}

export async function getSessionHandoff(): Promise<SessionHandoffResponse> {
  return get<SessionHandoffResponse>("/api/pwa/session-handoff");
}

export async function getOpsReport(): Promise<OpsReportResponse> {
  return get<OpsReportResponse>("/api/pwa/ops-report");
}

export async function getConsultationSummary(): Promise<ConsultationSummaryResponse> {
  return get<ConsultationSummaryResponse>("/api/pwa/consultation-summary");
}

export async function getDiagnosticReport(): Promise<DiagnosticReportResponse> {
  return get<DiagnosticReportResponse>("/api/pwa/diagnostic-report");
}

export const api = {
  health: () => get<HealthResponse>("/api/pwa/health"),
  dashboard: () => get<DashboardResponse>("/api/pwa/dashboard"),
  cost: () => get<CostResponse>("/api/pwa/cost"),
  costPacing: () => get<CostPacingData>("/api/pwa/cost-pacing"),
  componentHealth: () =>
    get<ComponentHealthResponse>("/api/pwa/component-health"),
  heroes: (asOf?: string | null) =>
    get<HeroesResponse>(withAsOf("/api/pwa/heroes", asOf)),
  snapshot: (asOf?: string | null) =>
    get<SnapshotResponse>(withAsOf("/api/pwa/snapshot", asOf)),
  getHomeDigest: () => get<HomeDigestResponse>("/api/pwa/home-digest"),
  releaseValidations: () =>
    get<ReleaseValidationsResponse>("/api/pwa/release-validations"),
  dailyDigest: (asOf?: string | null) =>
    get<DailyDigestResponse>(withAsOf("/api/pwa/daily-digest", asOf)),
};

export interface EntitySchemaType {
  type: string;
  label: string;
  description: string;
  idPattern: string;
  directory: string;
  format: string;
  keyFields: string[];
  validStatuses: string[];
  relatedTypes: string[];
}

export async function fetchEntitySchema(): Promise<{
  types: EntitySchemaType[];
}> {
  return get<{ types: EntitySchemaType[] }>("/api/pwa/entity-schema");
}

export async function getDispatchMetrics(options?: {
  include?: string[];
}): Promise<DispatchMetricsResponse> {
  const params = new URLSearchParams();
  if (options?.include && options.include.length > 0) {
    params.set("include", options.include.join(","));
  }
  const qs = params.toString();
  return get<DispatchMetricsResponse>(
    `/api/pwa/dispatch-metrics${qs ? `?${qs}` : ""}`,
  );
}

export async function getWaveFunnel(): Promise<WaveFunnelResponse> {
  return get<WaveFunnelResponse>("/api/pwa/wave-funnel");
}

export async function getDispatchHealth(): Promise<DispatchHealthResponse> {
  return get<DispatchHealthResponse>("/api/pwa/dispatch-health");
}

export async function getPhaseProgress(): Promise<PhaseProgressResponse> {
  return get<PhaseProgressResponse>("/api/pwa/phase-progress");
}

export async function fetchFrictionFunnel(): Promise<FrictionFunnelMetrics> {
  return get<FrictionFunnelMetrics>("/api/pwa/friction-funnel");
}

export async function fetchCIQueueDepth(): Promise<CIQueueDepthResponse> {
  return get<CIQueueDepthResponse>("/api/pwa/ci-queue-depth");
}
